import React, { FormEvent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';

import { CustomButton, Input, Modifiers } from '@fw-nextgen/fw-components';
import ReloadIcon from '@app/src/assets/icons/reload.svg';

import MfaChallengeStore from '../../MfaChallengeStore';
import AuthWindowLogo from '../../../components/AuthWindowLogo';

import './MfaCodeStep.scss';

interface IProps {
  store: MfaChallengeStore;
  onBack: () => void;
}

const MfaCodeStep = ({ store, onBack }: IProps) => {
  const { invalidSeq } = store;

  // Re-selects the rejected value so the next keystroke replaces it. Driven by a counter rather
  // than the error object so resubmitting the same wrong code still re-selects.
  useEffect(() => {
    if (invalidSeq) {
      store.selectCode();
    }
  }, [invalidSeq]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    store.verify();
  };

  return (
    <form className="auth-window__form mfa-code-step" onSubmit={onSubmit}>
      <AuthWindowLogo />
      <h5 className="auth-window__heading">
        <FormattedMessage id="auth.mfa.heading" />
      </h5>
      <div>
        <p className="auth-window__info">
          <FormattedMessage
            id={store.infoMessage}
            values={{
              email: <strong>{store.maskedEmail}</strong>,
              minutes: store.validityMinutes
            }}
          />
        </p>
        {/* No field to hang an inline error under while no code exists, and the outcome of a
            resend — a throttle, an expiry — still needs somewhere to be announced. */}
        {store.awaitingCode && store.error && (
          <p
            className="auth-window__info mfa-code-step__notice-error"
            role="alert"
          >
            <FormattedMessage
              id={store.error.message}
              values={store.error.params}
            />
          </p>
        )}
        {/* No code was ever sent for this challenge, so no entry could succeed: none is offered
            until a resend delivers one. */}
        {!store.awaitingCode && (
          <Input
            name="mfaCode"
            id="mfa-code"
            // No maxLength: the browser would enforce it on paste before onChange runs, so a code
            // pasted with a separator ("123 456") would be clipped to five digits. sanitizeCode
            // strips the separators and caps the length instead.
            type="tel"
            label="auth.mfa.code.label"
            classNameFormField="mfa-code-step__field"
            autoFocus
            autoComplete="one-time-code"
            textPlaceholder="123456"
            value={store.code}
            readOnly={store.verifying}
            disabled={!!store.terminal}
            error={store.error || undefined}
            onChange={store.setCode}
            setInputRef={store.setInputEl}
          />
        )}
        {store.showResend && (
          <div className="mfa-code-step__resend">
            {store.showResendLink ? (
              <button
                type="button"
                className="mfa-code-step__resend-link"
                disabled={!store.canResend}
                onClick={store.resend}
              >
                <ReloadIcon className="mfa-code-step__resend-icon" />
                <FormattedMessage id="auth.mfa.resend" />
              </button>
            ) : (
              <span>
                <FormattedMessage id="auth.mfa.resend.wait" />
                {' '}
                {store.resendCountdown}
              </span>
            )}
          </div>
        )}
      </div>
      <div>
        {!store.awaitingCode && (
          <CustomButton
            type="submit"
            loading={store.verifying}
            disabled={store.submitDisabled}
            modifiers={[Modifiers.SMALL]}
            className="auth-window__button"
            value="auth.mfa.submit"
          />
        )}
        <div className="mfa-code-step__back">
          <button
            type="button"
            className="auth-window__back-link mfa-code-step__back-button"
            onClick={onBack}
          >
            <FormattedMessage id="auth.mfa.back" />
          </button>
        </div>
      </div>
    </form>
  );
};

export default observer(MfaCodeStep);
